import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

const OPTIONS = [
  { key: "include_client", label: "Client details", hint: "Name, email, phone, site address" },
  { key: "include_items", label: "Line items & pricing", hint: "All sections, quantities, rates and margins" },
  { key: "include_scope", label: "AI scope descriptions", hint: "Customer-facing narratives on each line" },
  { key: "include_visuals", label: "Site photos & AI visuals", hint: "Before/after visual blocks" },
];

const ALL_ON = { include_client: true, include_items: true, include_scope: true, include_visuals: true };
const NO_DEAL = "__none__";

export default function DuplicateQuoteDialog({ quote, open, onClose }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [opts, setOpts] = useState(ALL_ON);
  const [dealId, setDealId] = useState(NO_DEAL);
  const [busy, setBusy] = useState(false);

  const { data: deals = [] } = useQuery({
    queryKey: ["deals-for-quote"],
    queryFn: async () => (await api.get("/deals")).data,
    enabled: open,
  });

  // Default the deal link to the ORIGINAL quote's deal every time the dialog opens.
  useEffect(() => {
    if (open) {
      setOpts(ALL_ON);
      setDealId(quote?.deal_id || NO_DEAL);
    }
  }, [open, quote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/duplicate`, {
        ...opts,
        deal_id: dealId === NO_DEAL ? null : dealId,
      });
      toast.success(`Copied to ${data.quote_number} — you're now editing the copy`);
      qc.invalidateQueries({ queryKey: ["quotes"] });
      onClose();
      nav(`/quotes/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not copy quote");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="duplicate-quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Copy size={18} className="text-cyan-600" /> Copy {quote?.quote_number || "quote"} to a new quote
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="stamp-label text-slate-600">Link the new quote to a deal</Label>
            <Select value={dealId} onValueChange={setDealId}>
              <SelectTrigger data-testid="dup-deal-select" className="mt-1 rounded-sm h-10">
                <SelectValue placeholder="Pick a deal…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_DEAL}>— No deal (standalone copy) —</SelectItem>
                {deals.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.job_number ? `${d.job_number} — ` : ""}{d.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-400 mt-1">
              {quote?.deal_id ? "Defaults to the original quote's deal — change it or pick standalone." : "The original quote isn't linked to a deal."}
            </p>
          </div>
          <div className="space-y-2">
            <Label className="stamp-label text-slate-600">What carries over</Label>
            {OPTIONS.map((o) => (
              <label key={o.key} className="flex items-start gap-3 border border-slate-200 rounded-sm p-3 cursor-pointer hover:bg-slate-50">
                <Checkbox
                  data-testid={`dup-${o.key}`}
                  checked={opts[o.key]}
                  onCheckedChange={(v) => setOpts({ ...opts, [o.key]: !!v })}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">{o.label}</span>
                  <span className="block text-xs text-slate-500">{o.hint}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="text-xs text-slate-500">
            The copy starts as a fresh <b>Draft</b> with a new quote number and no send / acceptance / Xero history.
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="confirm-duplicate-btn" onClick={run} disabled={busy} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
            {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
            Create copy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
